import React, { useState, useCallback } from "react";
import { css } from "@emotion/react";
import {
  PythonFrameworkId,
  getFrameworkMeta,
} from "./pythonAgentTemplates";

export interface PythonAgentTemplateModalProps {
  isOpen: boolean;
  frameworkId: PythonFrameworkId;
  onClose: () => void;
  onUseTemplatePath?: (path: string) => void;
}

// Crisp SVGs
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const TerminalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 17 10 11 4 5" />
    <line x1="12" y1="19" x2="20" y2="19" />
  </svg>
);

const FileCodeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <polyline points="10 13 8 15 10 17" />
    <polyline points="14 13 16 15 14 17" />
  </svg>
);

// Styles
const modalOverlay = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
`;

const modalContainer = css`
  background: #ffffff;
  border-radius: 14px;
  width: 100%;
  max-width: 860px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px -8px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  animation: modalEnter 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes modalEnter {
    from {
      opacity: 0;
      transform: scale(0.96) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .theme--dark & {
    background: #0f172a !important;
    border: 1px solid #334155 !important;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7) !important;
  }
`;

const modalHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
  }
`;

const headerTitleGroup = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const headerTitle = css`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 750;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const badgeStyle = css`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 0.74rem;
  font-weight: 700;
  border-radius: 9999px;
  background: #e0f2fe;
  color: #0284c7;
  border: 1px solid #bae6fd;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  .theme--dark & {
    background: #0369a1 !important;
    color: #e0f2fe !important;
    border-color: #0284c7 !important;
  }
`;

const headerSubtitle = css`
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const closeButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }

  .theme--dark & {
    color: #94a3b8 !important;
    &:hover {
      background: #334155 !important;
      color: #f8fafc !important;
    }
  }
`;

const modalBody = css`
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const sectionHeading = css`
  font-size: 0.84rem;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 8px 0;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const installBox = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: #0f172a;
  border-radius: 8px;
  border: 1px solid #1e293b;
  font-family: "Geist Mono", Menlo, Monaco, Consolas, monospace;
  font-size: 0.86rem;
  color: #38bdf8;
`;

const copyPillButton = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  font-size: 0.78rem;
  font-weight: 650;
  border-radius: 6px;
  border: 1px solid #334155;
  background: #1e293b;
  color: #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  white-space: nowrap;

  &:hover {
    background: #334155;
    color: #ffffff;
  }
`;

const stepsList = css`
  margin: 0;
  padding-left: 20px;
  font-size: 0.86rem;
  line-height: 1.6;
  color: #334155;

  li {
    margin-bottom: 4px;
  }

  .theme--dark & {
    color: #cbd5e1 !important;
  }
`;

const codeBoxContainer = css`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: #0f172a;

  .theme--dark & {
    border-color: #334155 !important;
  }
`;

const codeBoxHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: #1e293b;
  border-bottom: 1px solid #334155;
  font-size: 0.8rem;
  color: #94a3b8;
`;

const codePre = css`
  margin: 0;
  padding: 16px;
  max-height: 360px;
  overflow: auto;
  font-family: "Geist Mono", Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 0.84rem;
  line-height: 1.55;
  color: #f8fafc;
  background: #0f172a;
`;

const modalFooter = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  gap: 12px;

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
  }
`;

const actionButtonPrimary = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  font-size: 0.88rem;
  font-weight: 700;
  border-radius: 8px;
  border: 1px solid #0f766e;
  background: #0f766e;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #0d9488;
  }
`;

const actionButtonSecondary = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  font-size: 0.88rem;
  font-weight: 650;
  border-radius: 8px;
  border: 1.5px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  .theme--dark & {
    background: #1e293b !important;
    border-color: #475569 !important;
    color: #cbd5e1 !important;
    &:hover {
      background: #334155 !important;
      color: #ffffff !important;
    }
  }
`;

export function PythonAgentTemplateModal({
  isOpen,
  frameworkId,
  onClose,
  onUseTemplatePath,
}: PythonAgentTemplateModalProps) {
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedInstall, setCopiedInstall] = useState(false);

  const meta = getFrameworkMeta(frameworkId);

  // Copy template handler
  const handleCopyCode = useCallback(() => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(meta.codeTemplate);
    } else {
      const el = document.createElement("textarea");
      el.value = meta.codeTemplate;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  }, [meta.codeTemplate]);

  // Copy install command handler
  const handleCopyInstall = useCallback(() => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(meta.installCommand);
    }
    setCopiedInstall(true);
    setTimeout(() => setCopiedInstall(false), 2000);
  }, [meta.installCommand]);

  // Download Python file handler
  const handleDownloadFile = useCallback(() => {
    const blob = new Blob([meta.codeTemplate], { type: "text/x-python;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = meta.defaultFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [meta.codeTemplate, meta.defaultFilename]);

  if (!isOpen) return null;

  return (
    <div css={modalOverlay} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="template-modal-title">
      <div css={modalContainer} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div css={modalHeader}>
          <div css={headerTitleGroup}>
            <h2 id="template-modal-title" css={headerTitle}>
              <FileCodeIcon />
              <span>{meta.name} Quick-Start Template</span>
              <span css={badgeStyle}>{meta.badge}</span>
            </h2>
            <p css={headerSubtitle}>
              Template file: <strong>{meta.defaultFilename}</strong> &bull; Conforms to <code>call_api</code> contract
            </p>
          </div>
          <button type="button" css={closeButton} onClick={onClose} title="Close" aria-label="Close template dialog">
            <CloseIcon />
          </button>
        </div>

        {/* Modal Body */}
        <div css={modalBody}>
          {/* Section 1: Prerequisites & Installation */}
          <div>
            <h3 css={sectionHeading}>
              <TerminalIcon />
              <span>1. Required Installation</span>
            </h3>
            <div css={installBox}>
              <span>{meta.installCommand}</span>
              <button type="button" css={copyPillButton} onClick={handleCopyInstall}>
                {copiedInstall ? <CheckIcon /> : <CopyIcon />}
                <span>{copiedInstall ? "Copied!" : "Copy"}</span>
              </button>
            </div>
          </div>

          {/* Section 2: Next Steps */}
          <div>
            <h3 css={sectionHeading}>
              <span>2. Next Steps</span>
            </h3>
            <ol css={stepsList}>
              {meta.nextSteps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </div>

          {/* Section 3: Generated Python Source Code Preview */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <h3 css={sectionHeading} style={{ margin: 0 }}>
                <FileCodeIcon />
                <span>3. Python Source Code Preview</span>
              </h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <button type="button" css={copyPillButton} onClick={handleCopyCode}>
                  {copiedCode ? <CheckIcon /> : <CopyIcon />}
                  <span>{copiedCode ? "Copied!" : "Copy Template"}</span>
                </button>
                <button type="button" css={copyPillButton} onClick={handleDownloadFile}>
                  <DownloadIcon />
                  <span>Download .py</span>
                </button>
              </div>
            </div>

            <div css={codeBoxContainer}>
              <div css={codeBoxHeader}>
                <span style={{ fontWeight: 650, color: "#f8fafc" }}>{meta.defaultFilename}</span>
                <span>Python 3 &bull; call_api</span>
              </div>
              <pre css={codePre}>
                <code>{meta.codeTemplate}</code>
              </pre>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div css={modalFooter}>
          <div>
            {onUseTemplatePath && (
              <button
                type="button"
                css={actionButtonSecondary}
                onClick={() => {
                  onUseTemplatePath(meta.defaultFilePath);
                  onClose();
                }}
                title={`Set file path to ${meta.defaultFilePath}`}
              >
                <span>Use Path: {meta.defaultFilePath}</span>
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button type="button" css={actionButtonSecondary} onClick={handleCopyCode}>
              {copiedCode ? <CheckIcon /> : <CopyIcon />}
              <span>{copiedCode ? "Copied!" : "Copy Template"}</span>
            </button>
            <button type="button" css={actionButtonPrimary} onClick={handleDownloadFile}>
              <DownloadIcon />
              <span>Download Python File</span>
            </button>
            <button
              type="button"
              css={actionButtonSecondary}
              onClick={onClose}
              style={{ background: "#f1f5f9" }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
